/**
 * UserSwitcher
 *
 * Displays the authenticated user's name and role with a logout button.
 */

import { ChevronDown, LogOut, User } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAuthStore } from '@/lib/store/auth-slice';
import { logout } from '@/lib/api/client';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { usePiiMask } from '@/lib/hooks/use-pii-mask';

const ROLE_LABELS: Record<string, string> = {
  OPS_CONTROLLER:        'Operations Controller',
  STATION_MANAGER:       'Station Manager',
  HOTEL_VENDOR_USER:     'Hotel Vendor',
  TRANSPORT_VENDOR_USER: 'Transport Vendor',
  CREW:                  'Crew Member',
  FINANCE:               'Finance',
  SECURITY_REVIEWER:     'Security Reviewer',
  TENANT_ADMIN:          'Tenant Admin',
  SUPER_ADMIN:           'Super Admin',
};

export function UserSwitcher() {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const pii = usePiiMask();

  // If no authenticated user, don't render anything
  if (!authUser) {
    return null;
  }

  const roleLabel = ROLE_LABELS[authUser.role] ?? authUser.role;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-9 items-center gap-2.5 rounded-sm border border-border bg-background px-2.5 text-left transition-colors hover:bg-muted focus:outline-none focus:ring-1 focus:ring-qr-maroon"
          aria-label="User menu"
        >
          {/* User Icon */}
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-qr-maroon">
            <User className="h-3.5 w-3.5 text-white" />
          </div>

          {/* User info */}
          <div className="flex flex-col leading-tight">
            <span className="text-[12px] font-semibold text-foreground">{pii.userName(authUser)}</span>
            <span className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">
              {authUser.role.replace(/_/g, ' ')}
            </span>
          </div>

          {/* Chevron */}
          <ChevronDown className="ml-0.5 h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        {/* User info header */}
        <div className="flex items-center gap-3 px-3 py-3">
          {/* User Icon */}
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-qr-maroon">
            <User className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-semibold text-foreground">{pii.userName(authUser)}</div>
            <div className="truncate text-xs text-muted-foreground">
              {authUser.role === 'CREW' ? pii.email(authUser.email) : authUser.email}
            </div>
            <div className="mt-1">
              <span className="inline-block rounded-sm bg-qr-maroon px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-white">
                {roleLabel}
              </span>
            </div>
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Logout button */}
        <DropdownMenuItem
          onSelect={async () => {
            await logout();
            navigate('/login', { replace: true });
          }}
          className="flex cursor-pointer items-center gap-2 px-3 py-2 text-ops-danger transition-colors hover:bg-ops-danger/10 focus:bg-ops-danger/10"
        >
          <LogOut className="h-4 w-4" />
          <span className="text-[13px] font-medium">Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* Static user list - commented out for reference
{users.map((u) => {
  const active = u.id === activeUser.id;
  return (
    <DropdownMenuItem
      key={u.id}
      onSelect={() => setActiveUser(u.id)}
      className={cn(
        'flex cursor-pointer items-start gap-2 py-2',
        active && 'bg-qr-maroon/8',
      )}
    >
      <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold uppercase text-muted-foreground">
        {u.full_name.split(' ').map((s) => s[0]).slice(0, 2).join('')}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-medium text-foreground">{u.full_name}</span>
          {active && <span className="rounded-sm bg-qr-maroon px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">Active</span>}
        </div>
        <div className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">
          {u.role} · {ROLE_BLURB[u.role] ?? '—'}
        </div>
      </div>
    </DropdownMenuItem>
  );
})}
*/
